/**
 * The inspector (spec 2026-08-22-02 §9) — selection-driven, right-hand panel.
 *
 * Numeric fields are millimetres and follow the repo's inline-edit contract:
 * Enter commits, Escape reverts, blur commits. Every edit goes through the
 * engine's `updateElement`, so it lands in one undo step.
 */
import { AssetScopeChip } from '../AssetScopeChip'
import { matchesWithPath } from '../../lib/assetPath'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import {
    mdiAlertCircleOutline,
    mdiCallSplit,
    mdiContentDuplicate,
    mdiLayersTriple,
    mdiLinkVariant,
    mdiPencilOutline,
    mdiSetMerge,
    mdiSwapHorizontal,
    mdiTrashCanOutline,
} from '@mdi/js'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { useApi } from '../../api/hooks'
import { dist, formatAreaM2, formatLengthMm, shoelace } from './engine/geometry'
import { UncalibratedBadge } from './UncalibratedBadge'
import { NetworkSelect, RiserPassageSelect, RunLevelSwitch, RunWidthField } from './NetworkControls'
import {
    DIRECTIONAL_OPERATIONS,
    OPERATIONS,
    SWINGS,
    flipOpeningSide,
    normalizeOpeningSemantics,
    type Operation,
    type Swing,
} from './engine/openings'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import {
    GROUPER_KINDS,
    GROUPER_TYPE_CODES,
    grouperKindOf,
    grouperKindSpec,
    isGrouperElement,
    ownerName,
    ownerRoomNumber,
    ownerTypeCode,
    removeHole,
    runLength,
    spaceArea,
    spaceFallbackName,
    spaceHoles,
    type EditorElement,
    type NetworkLevel,
    type PlanNetwork,
    type GrouperKindId,
    type GrouperRef,
    type RunElement,
} from './engine/scene'
import type { Point } from './engine/geometry'
import { NETWORK_LEVELS, type LevelParents, type RunTarget, type SegmentAssembly } from './engine/network-assets'
import { NetworkAssemblyControl } from '../plan/NetworkAssemblyControl'
import { AssemblyControl } from '../plan/AssemblyControl'
import { typeAnchorsFrame } from './engine/parents'
import { useCan } from '../../contexts/PermissionsContext'
import type { GrouperOption } from './PlanEditorPage'
import { visibleRecentGroupers } from './useRecentGroupers'
import { PlanEquipmentTypePicker, type PickedEquipmentType } from './EquipmentTypePicker'
import { EquipmentSizeChips } from './EquipmentSizeChips'
import { WallThicknessChips } from './WallThicknessChips'
import type { EquipmentSize } from './useEquipmentSizes'
import { lotBaseTypeCode } from '../../lib/asset-type-tree'

/**
 * One drawing of the selected element's asset (spec 2026-08-25-04 §5). The
 * elevator parented at the building and drawn on floors 0–4 has five of them;
 * the one whose `current` flag is set is the layer being edited.
 */
export interface SharedLayer {
    repUid: string
    frameUid: string
    frameName: string
    current: boolean
}

interface InspectorPanelProps {
    org: string
    /** The plan's building: link / re-parent searches are scoped to it (`ancestor_uid`), with a "Search everywhere" escape. */
    scopeUid?: string
    scopeLabel?: string
    /** Ancestor uids (root first) of the plan's asset: the path part shared with it is hidden from the scoped search rows (the full path stays on the picked label). */
    sharedAncestorUids?: string[]
    element: EditorElement | null
    /** The network catalogue a run is described with, and its labels. */
    networks?: PlanNetwork[]
    networkLabelOf?: (code: string) => string
    /** Asset types a run may be linked to — the ontology's `*_network` family. */
    networkAssetTypeCodes?: string[]
    /**
     * What a run's network asset search covers: the site, so the networks of
     * the floor, its building and its site all show, floor first (spec
     * 2026-10-20-33).
     */
    networkScopeUid?: string
    networkScopeLabel?: string
    /** The floor, building and site a run's network asset can be filed under. */
    runLevelParents?: LevelParents
    /** The level a run files its network asset at. */
    runLevelOf?: (run: RunElement) => NetworkLevel | undefined
    /** The asset an unlinked run will depict once saved, reused or created. */
    runTargetOf?: (run: RunElement) => RunTarget | undefined
    /** Remembers a level picked for a network, for the next runs drawn. */
    onRunLevelPicked?: (network: string, level: NetworkLevel) => void
    /**
     * The building network a new floor segment joins on save (spec
     * 2026-10-20-35), and the name Move to building level gives the building
     * network it creates.
     */
    runSegmentAssemblyOf?: (run: RunElement) => SegmentAssembly | undefined
    runAssemblyNameOf?: (run: RunElement) => string | undefined
    /**
     * Where a column already crosses the building's other floors, mapped into
     * this plan (spec 2026-10-19-03 §1): linking a riser to it places the riser
     * there. Undefined when the column is drawn on no other floor.
     */
    riserAlignmentFor?: (columnUid: string) => { point: Point; levelName: string } | undefined
    /** A segment joined or left its building network. */
    onNetworkAssemblyChanged?: () => void
    openingTypes: { code: string; label: string }[]
    /**
     * `room` and every sub-type of it the organization declares (spec
     * 2026-08-31-01 §7) — the options of a drawn space's Type select, in the
     * ontology's own order.
     */
    roomTypes: { code: string; label: string }[]
    /**
     * The asset type codes that carry a `room_number` — read from the ontology
     * by the page (spec 2026-08-31-02 §6), so which rooms are numbered is a
     * provisioning question and not a list in here.
     */
    roomNumberTypes: Set<string>
    /** The number a room of this type under this parent should take next. */
    nextRoomNumber: (typeCode: string, parentUid?: string) => number
    /** Applies a mutation to **every** selected element inside one undo step. */
    onUpdate: (patch: (el: EditorElement) => void) => void
    /**
     * The same, on the **primary alone** (spec 2026-09-06-01 §1). A category
     * group may hold a radiator and a boiler, or an entry door and an interior
     * one: the fields they do not share in meaning — the type, and the sizes
     * remembered for that type — go through here, so a group edit can never
     * write one member's type onto the others.
     */
    onUpdatePrimary: (patch: (el: EditorElement) => void) => void
    /**
     * True when the selection holds more than one asset type. Only the
     * type-specific controls change behaviour; everything else still edits the
     * whole group.
     */
    mixedTypes: boolean
    onDelete: () => void
    onDuplicate: () => void
    /**
     * Attach the geometry to an existing asset instead of creating one. The
     * raw record travels with it: the page needs the asset's type and parent
     * to decide whether this footprint is cross-layer (spec 2026-08-25-04 §1),
     * and a linked asset is by definition outside the frame's own index.
     */
    onLinkAsset: (
        assetUid: string,
        assetName: string,
        asset?: { asset_type_uid?: string; asset_type_code?: string; parent_asset_uid?: string },
    ) => void
    onPickEquipmentType: (picked: PickedEquipmentType) => void
    /** Localized display name of an asset type code. */
    typeLabelOf: (code: string) => string
    /** Controlled by the page so placing an equipment can open the picker itself. */
    pickerOpen: boolean
    onPickerOpenChange: (next: boolean) => void
    /** True when the element sits in a bundle and can get its own asset (§3). */
    promotable: boolean
    onPromote: () => void
    /**
     * How many spaces and openings "Promote all" would extract from the
     * selected element's bundle (spec 2026-09-03-01 §F5). Zero hides the
     * button.
     */
    promoteAllCount?: number
    onPromoteAll?: () => void
    /**
     * §5a: the selected element's own validation issues, and whether ANY
     * error-level issue remains anywhere in the bundle. An error does not
     * discard the geometry — it blocks the one action that would write it into
     * the inventory wholesale.
     */
    elementIssues?: { code: string; severity: string; params?: Record<string, string | number> }[]
    promoteAllBlocked?: boolean
    /**
     * One line saying this geometry was digitized, and from what — shown on
     * every element of a bundle a `floorplan_digitize` run produced (§F4).
     */
    provenance?: string | null
    /** Representations the element could be merged back into (§4). */
    demoteTargets: { repUid: string; label: string; elementCount: number }[]
    onDemote: (repUid: string) => void
    /**
     * Wall bundles a selected wall can be moved to (§5). `crossFrame` marks an
     * owner whose implicit frame is not the edited one, which the guard of §6
     * confirms before the wall follows it there.
     */
    wallOwners: { uid: string; name: string; crossFrame?: boolean }[]
    onRetargetWall: (assetUid: string) => void
    /**
     * Commit a wall's thickness. Separate from `onUpdate` because the value is
     * also the sticky default the wall tool draws with next.
     */
    onWallThickness: (mm: number) => void
    /** The plan's recent thicknesses, offered here as well as on the wall tool. */
    recentThicknesses: number[]
    /** How many elements these fields are editing — more than one after a
     *  shift-click, and every edit below then applies to all of them. */
    selectionCount: number
    /** This equipment type's recent footprints, and where a pick lands. */
    recentSizes: EquipmentSize[]
    onEquipmentSize: (size: EquipmentSize) => void
    /**
     * A pick from the **remembered sizes**, which belong to the primary's type
     * — so on a mixed selection it lands on the primary alone. The width and
     * depth fields above them stay a group edit: every equipment has a
     * footprint, and it means the same thing on all of them.
     */
    onEquipmentSizePreset: (size: EquipmentSize) => void
    /**
     * "You dropped it in another room — file it there?". Set only just after a
     * move that landed somewhere other than the current parent.
     */
    /**
     * The units and common areas of this building group, plus the ones this
     * session created but has not saved yet — everything the grouping combobox
     * may offer (spec 2026-08-30-01 §5).
     */
    grouperOptions: GrouperOption[]
    /**
     * The last few groupers assigned through this picker, most recent first
     * (spec 2026-09-06-06) — rendered as a shortcut group above the
     * type-sectioned list, in the order `useRecentGroupers` remembers them.
     */
    recentGrouperUids: string[]
    /**
     * A localized grouping problem to show **on the field** — the duplicate
     * name the API refused, or the `group_member` edge it could not write. Cleared
     * as soon as the user touches the combobox again.
     */
    grouperError?: string
    /**
     * A message for the **Code** field — an asset code is an external id, and
     * those are unique organization-wide (spec 2026-08-30-03 §3), so a code
     * already used by another asset comes back refused.
     */
    grouperCodeError?: string | null
    /**
     * Changes when a space is double-clicked on the plan: put the cursor in the
     * name and select what is there, so the new name replaces the old one by
     * being typed (spec 2026-08-30-03 §13).
     */
    focusNameNonce?: number
    /** Enter in the name field commits the edit the way Save does. */
    onSaveRequested?: () => void
    /** File the selection under a grouper, or clear it with `null`. */
    onAssignGrouper: (ref: GrouperRef | null) => void
    /** Create a grouper from the typed text and assign it in the same gesture. */
    onCreateGrouper: (name: string, typeCode: string) => void
    parentSuggestion: { parentUid?: string; parentKey?: string; name: string } | null
    onAcceptParentSuggestion: () => void
    onDismissParentSuggestion: () => void
    /**
     * The frame's millimetres-per-plan-unit, or null when it is not to scale
     * (spec 2026-08-25-04 §3). Every readout below multiplies through it.
     */
    mmPerUnit: number | null
    /**
     * Plan units to millimetres and back, for the fields that show a size.
     * Geometry is in plan units; a thickness or width is read and typed in mm.
     */
    toMm: (units: number) => number
    toUnits: (mm: number) => number
    /** Every layer the selected element's asset is drawn on (§5). */
    sharedLayers: SharedLayer[]
    /** True while the shell is still resolving the other layers. */
    sharedLoading?: boolean
    /** Navigate to another layer's editor. */
    onOpenLayer: (frameUid: string) => void
    /** The resolved parent of the element's asset, for display (§2). */
    parent: { uid?: string; name: string } | null
    /**
     * Override the resolved parent. For a pending asset this is a local edit;
     * for an existing one it re-parents it on save, so the panel confirms first
     * — the asset's implicit frame follows its parent (§6).
     */
    onParentChange: (parentUid: string, parentName: string) => void
}

/** Millimetre field: Enter commits, Escape reverts, blur commits. */
/** Asset search menus in the inspector: wide enough for a full lot name, never wider than the screen. */
const WIDE_ASSET_MENU = 'w-[28rem] max-w-[calc(100vw-2rem)]'

function NumberField(props: {
    id: string
    label: string
    value: number
    step?: number
    min?: number
    onCommit: (value: number) => void
}) {
    const [draft, setDraft] = useState(String(props.value))
    // Enter commits and then blurs, and the blur commits again — inside the
    // same tick, so `props.value` is still the old one and the guard below
    // would let the second through. One gesture must be one change: two of them
    // means two entries in the undo history, and a first Ctrl+Z that looks
    // broken because it reverts a no-op.
    const committed = useRef(props.value)
    useEffect(() => {
        committed.current = props.value
        setDraft(String(props.value))
    }, [props.value])

    const commit = () => {
        const parsed = Number(draft)
        if (!Number.isFinite(parsed)) {
            setDraft(String(committed.current))
            return
        }
        const clamped = props.min !== undefined ? Math.max(props.min, parsed) : parsed
        if (clamped !== committed.current) {
            committed.current = clamped
            props.onCommit(clamped)
        }
        setDraft(String(clamped))
    }

    return (
        <div className="flex items-center gap-2 py-1">
            <Label htmlFor={props.id} className="w-28 shrink-0 text-xs text-muted-foreground">
                {props.label}
            </Label>
            <Input
                id={props.id}
                data-testid={props.id}
                type="number"
                className="h-7 text-xs"
                step={props.step ?? 1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault()
                        commit()
                        ;(e.target as HTMLInputElement).blur()
                    } else if (e.key === 'Escape') {
                        e.preventDefault()
                        setDraft(String(props.value))
                        ;(e.target as HTMLInputElement).blur()
                    }
                }}
            />
        </div>
    )
}

function SelectField(props: {
    id: string
    label: string
    value: string
    options: { value: string; label: string }[]
    onChange: (value: string) => void
    /** Trailing badge — the "N selected, mixed" hint on a type-specific field. */
    after?: ReactNode
    /** The org's ontology has nothing to pick from (spec 2026-09-14-02) — shown, not hidden. */
    disabled?: boolean
}) {
    return (
        <div className="flex flex-wrap items-center gap-2 py-1">
            <Label htmlFor={props.id} className="w-28 shrink-0 text-xs text-muted-foreground">
                {props.label}
            </Label>
            <select
                id={props.id}
                data-testid={props.id}
                className="h-7 flex-1 rounded-md border border-input bg-background px-2 text-xs text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                value={props.value}
                disabled={props.disabled}
                onChange={(e) => props.onChange(e.target.value)}
            >
                {props.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
            {props.after}
        </div>
    )
}

/**
 * The **Grouping** combobox (spec 2026-08-30-01 §5).
 *
 * A drawn space says which flat or common area it belongs to, and nothing
 * moves in the asset tree: the assignment writes a `group_member` edge, and the room
 * stays a child of its floor. The list is sectioned by family, filters as you
 * type, and offers to create what you typed when nothing matches — inline,
 * because the alternative is leaving the plan to go and make a lot.
 *
 * With several spaces selected the assignment lands on all of them at once.
 */
function SpaceGrouperField(props: {
    grouper?: GrouperRef
    error?: string
    options: GrouperOption[]
    /** Most recent first (spec 2026-09-06-06) — hidden while `typed` is non-empty. */
    recentUids: string[]
    typeLabelOf: (code: string) => string
    onAssign: (ref: GrouperRef | null) => void
    onCreate: (name: string, typeCode: string) => void
}) {
    const { t } = useTranslation('assets')
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')

    const typed = query.trim()
    const needle = typed.toLowerCase()
    const matches = props.options.filter(
        (o) => !needle || o.name.toLowerCase().includes(needle) || (o.code ?? '').toLowerCase().includes(needle),
    )
    const sections = GROUPER_TYPE_CODES.map((code) => ({
        code,
        items: matches.filter((o) => lotBaseTypeCode(o.typeCode) === code),
    })).filter((sec) => sec.items.length > 0)
    // A query is an explicit request that beats recency — and it keeps the
    // "exact name → pick, not create" rule below untouched.
    const recent = typed ? [] : visibleRecentGroupers(props.recentUids, props.options)
    // An exact name already in the list is a pick, not a creation — offering
    // "Create Lot B02" next to the Lot B02 that exists is how duplicates get
    // made (the API refuses them, but the user should not have to find out).
    const exact = props.options.some((o) => o.name.trim().toLowerCase() === needle)

    const pick = (option: GrouperOption) => {
        props.onAssign(
            option.uid.startsWith('pending:')
                ? { assetKey: option.uid.slice('pending:'.length), name: option.name, typeCode: option.typeCode }
                : { assetUid: option.uid, name: option.name, typeCode: option.typeCode },
        )
        setOpen(false)
        setQuery('')
    }
    const create = (typeCode: string) => {
        props.onCreate(typed, typeCode)
        setOpen(false)
        setQuery('')
    }

    return (
        <div className="flex flex-col gap-1 py-1">
            <div className="flex items-center gap-2">
            <Label className="w-28 shrink-0 text-xs text-muted-foreground">{t('planEditor.field_grouping')}</Label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className={`h-7 flex-1 justify-start px-2 text-xs font-normal${props.error ? ' border-destructive' : ''}`}
                        data-testid="plan-editor-space-grouper"
                        aria-label={t('planEditor.field_grouping')}
                        aria-invalid={props.error ? true : undefined}
                    >
                        {props.grouper?.name || (
                            // Never a save blocker: a space with no grouper is
                            // a perfectly valid space, so this is a hint and
                            // not a warning.
                            <span className="text-muted-foreground">{t('planEditor.grouping_none')}</span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={t('planEditor.grouping_search')}
                            value={query}
                            onValueChange={setQuery}
                            data-testid="plan-editor-space-grouper-search"
                        />
                        <CommandList>
                            {!sections.length && !typed && (
                                <CommandEmpty>{t('planEditor.grouping_empty')}</CommandEmpty>
                            )}
                            {props.grouper && (
                                <CommandGroup>
                                    <CommandItem
                                        value="__clear"
                                        onSelect={() => {
                                            props.onAssign(null)
                                            setOpen(false)
                                            setQuery('')
                                        }}
                                        data-testid="plan-editor-space-grouper-clear"
                                    >
                                        {t('planEditor.grouping_clear')}
                                    </CommandItem>
                                </CommandGroup>
                            )}
                            {recent.length > 0 && (
                                <CommandGroup heading={t('planEditor.grouping_recent')} data-testid="plan-editor-space-grouper-recent-group">
                                    {recent.map((option) => (
                                        <CommandItem
                                            key={`recent-${option.uid}`}
                                            value={`recent-${option.uid}`}
                                            onSelect={() => pick(option)}
                                            data-testid={`plan-editor-space-grouper-recent-${option.uid}`}
                                        >
                                            <span className="truncate">{option.name}</span>
                                            {option.code && (
                                                <span className="ml-2 shrink-0 text-muted-foreground">{option.code}</span>
                                            )}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                            {sections.map((sec) => (
                                <CommandGroup key={sec.code} heading={props.typeLabelOf(sec.code)}>
                                    {sec.items.map((option) => (
                                        <CommandItem
                                            key={option.uid}
                                            value={option.uid}
                                            onSelect={() => pick(option)}
                                            data-testid={`plan-editor-space-grouper-option-${option.uid}`}
                                        >
                                            <span className="truncate">{option.name}</span>
                                            {option.code && (
                                                <span className="ml-2 shrink-0 text-muted-foreground">{option.code}</span>
                                            )}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            ))}
                            {typed && !exact && (
                                <CommandGroup heading={t('planEditor.grouping_create_heading')}>
                                    <CommandItem
                                        value="__create-unit"
                                        // A lot made to group rooms is a housing (spec 2026-10-09-04).
                                        onSelect={() => create('unit_housing')}
                                        data-testid="plan-editor-space-grouper-create-unit"
                                    >
                                        {t('planEditor.grouping_create_unit', { name: typed })}
                                    </CommandItem>
                                    <CommandItem
                                        value="__create-common"
                                        onSelect={() => create('common_area')}
                                        data-testid="plan-editor-space-grouper-create-common"
                                    >
                                        {t('planEditor.grouping_create_common', { name: typed })}
                                    </CommandItem>
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            </div>
            {/* Inline, next to the name that caused it — the save summary strip
              * says a save failed, this says what to do about it. */}
            {props.error && (
                <p
                    className="text-[11px] leading-snug text-destructive"
                    role="alert"
                    data-testid="plan-editor-space-grouper-error"
                >
                    {props.error}
                </p>
            )}
        </div>
    )
}

export function InspectorPanel(props: InspectorPanelProps) {
    const { t } = useTranslation('assets')
    const { request } = useApi()
    const { element } = props
    // Gates the "add them from the default types" link on the degraded room
    // type field below — only someone who could actually act on it sees it.
    const canProvisionRoomTypes = useCan('configuration.write')
    const [linking, setLinking] = useState(false)
    const [merging, setMerging] = useState(false)
    const [reparenting, setReparenting] = useState(false)
    // §6: re-parenting an *existing* asset moves its implicit frame with it, so
    // the change waits behind a confirm rather than landing on a click.
    const nameInputRef = useRef<HTMLInputElement>(null)
    /** True while the name field is open *because* a shape was double-clicked. */
    const [renaming, setRenaming] = useState(false)
    // A double click on the plan lands here: focus the field and select it, so
    // the next keystroke replaces the name rather than appending to it.
    const { focusNameNonce } = props
    useEffect(() => {
        if (!focusNameNonce) return
        // One frame later: the double click that asks for this also *selects*
        // the shape, so the field may only be mounting in this very commit and
        // the ref is still empty when the effect first runs.
        const frame = requestAnimationFrame(() => {
            const input = nameInputRef.current
                ?? document.querySelector<HTMLInputElement>('[data-testid="plan-editor-field-room-name"]')
            input?.focus()
            input?.select()
            setRenaming(true)
        })
        return () => cancelAnimationFrame(frame)
    }, [focusNameNonce])

    // The gesture ends with the field: leaving it, or selecting something else,
    // puts Enter back to being "commit and leave".
    useEffect(() => setRenaming(false), [element?.key])

    const [pendingReparent, setPendingReparent] = useState<
        { uid: string; name: string; intent: 'parent' | 'wall_owner' } | null
    >(null)

    /**
     * The selected grouper as the asset lists know it — its stored code and
     * kind (spec 2026-08-30-03 §4). Read from the live list rather than the
     * document, because the lists settle on their own schedule and the
     * document only ever carries what this session edited.
     */
    const storedGrouper = element?.owner.assetUid
        ? props.grouperOptions.find((o) => o.uid === element.owner.assetUid)
        : undefined

    /** The selected space's room type, and what it reads as when unnamed. */
    const roomTypeCode = element?.kind === 'space' ? ownerTypeCode(element.owner) : undefined
    const roomFallbackLabel = element?.kind === 'space'
        ? spaceFallbackName(element.owner, props.typeLabelOf)
        : ''

    /**
     * Same fallback chain the (enabled) room-type select reads its value
     * from, hoisted so the degraded/disabled branch can show the current
     * type without duplicating the promotion logic (spec 2026-09-14-02).
     */
    const roomTypeSelectValue = element?.kind === 'space'
        ? (element.owner.newAsset
            ? element.owner.newAsset.typeCode
            : element.owner.assetTypeEdit
            ?? (element.owner.assetUid ? element.owner.assetTypeCode : undefined)
            ?? element.typeHint
            ?? element.owner.assetTypeCode ?? '')
        : ''

    /**
     * Options for the disabled room-type select: the org's (possibly
     * single-entry) room types, plus the current value itself if the
     * ontology doesn't know it — so the field never renders empty.
     */
    const { roomTypes, typeLabelOf } = props
    const roomTypeDisabledOptions = useMemo(() => {
        const base = roomTypes.map((r) => ({ value: r.code, label: r.label }))
        if (!roomTypeSelectValue || base.some((o) => o.value === roomTypeSelectValue)) return base
        return [...base, { value: roomTypeSelectValue, label: typeLabelOf(roomTypeSelectValue) }]
    }, [roomTypes, typeLabelOf, roomTypeSelectValue])

    // Scoped to the plan's floor or building; the user can widen it per search.
    const [widened, setWidened] = useState(false)
    const scopeUid = widened ? undefined : props.scopeUid
    const { sharedAncestorUids } = props
    const searchAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            const scope = scopeUid ? { ancestor_uid: scopeUid } : {}
            const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                params: {
                    path: { orgCode: props.org },
                    query: query ? { q: query, limit: '20', sort: 'relevance', asset_chain: 'true', ...scope } : { limit: '20', sort: '-updated_at', asset_chain: 'true', ...scope },
                },
            })
            const assets = (response as { data?: { uid: string; name?: string; asset_type_uid?: string; parent_asset_uid?: string; parent_chain?: boolean }[] })?.data ?? []
            // Once widened the results leave the perimeter: the prefix is information again.
            const shared = scopeUid ? sharedAncestorUids : undefined
            return matchesWithPath(assets, shared).map(({ row, label, fullLabel }) => ({ value: row.uid, label, data: { ...row, fullLabel } as Record<string, unknown> }))
        } catch {
            return []
        }
    }, [request, props.org, scopeUid, sharedAncestorUids])

    /**
     * The same search, restricted to network assets — what a run may depict —
     * filed directly under the run's floor, its building or its site, in that
     * order (spec 2026-10-20-33). Another building's networks stay out until
     * the manager widens the search to the whole organization.
     */
    const networkTypeFilter = (props.networkAssetTypeCodes ?? []).join(',')
    const networkScopeUid = widened ? undefined : (props.networkScopeUid ?? props.scopeUid)
    const levelParentKey = NETWORK_LEVELS.map((l) => props.runLevelParents?.[l]?.uid).filter(Boolean).join(',')
    const searchNetworkAssets = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        type Row = { uid: string; name?: string; asset_type_uid?: string; parent_asset_uid?: string; parent_chain?: boolean }
        const fetchRows = async (scope: Record<string, string>): Promise<Row[]> => {
            const types = networkTypeFilter ? { asset_type_code: networkTypeFilter } : {}
            const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                params: {
                    path: { orgCode: props.org },
                    query: query
                        ? { q: query, limit: '20', sort: 'relevance', asset_chain: 'true', ...scope, ...types }
                        : { limit: '20', sort: '-updated_at', asset_chain: 'true', ...scope, ...types },
                },
            })
            return (response as { data?: Row[] })?.data ?? []
        }
        try {
            const levelUids = networkScopeUid && levelParentKey ? levelParentKey.split(',') : []
            const assets = levelUids.length > 0
                ? (await Promise.all(levelUids.map((uid) => fetchRows({ parent_asset_uid: uid })))).flat()
                : await fetchRows(networkScopeUid ? { ancestor_uid: networkScopeUid } : {})
            const shared = networkScopeUid ? sharedAncestorUids : undefined
            return matchesWithPath(assets, shared)
                .map(({ row, label, fullLabel }) => ({ value: row.uid, label, data: { ...row, fullLabel } as Record<string, unknown> }))
        } catch {
            return []
        }
    }, [request, props.org, networkScopeUid, sharedAncestorUids, networkTypeFilter, levelParentKey])
    const [linkingRun, setLinkingRun] = useState(false)
    useEffect(() => setLinkingRun(false), [element?.key])
    const [riserAlignedOn, setRiserAlignedOn] = useState<string | null>(null)
    useEffect(() => setRiserAlignedOn(null), [element?.key])

    if (!element) {
        return (
            <div className="p-3 text-xs text-muted-foreground" data-testid="plan-editor-inspector">
                {t('planEditor.inspector_empty')}
            </div>
        )
    }

    // A room nobody named is still announced by what it *is* — the link at the
    // top of the panel reads "Chambre 2" rather than a uid (spec 2026-08-31-02),
    // and an auto-named one reads its *live* derivation, so retyping it in the
    // panel moves the link before the save does.
    // "3 selected, mixed" — shown beside a field that is *not* a group edit,
    // so the user can see which one the value belongs to (spec §1).
    const mixedHint = props.mixedTypes && props.selectionCount > 1 ? (
        <span
            className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
            data-testid="plan-editor-mixed-hint"
            title={t('planEditor.selection_mixed_hint')}
        >
            {t('planEditor.selection_mixed', { count: props.selectionCount })}
        </span>
    ) : null
    // A type-specific field edits the primary alone once the group is mixed;
    // with one type throughout, retyping the whole group is exactly what the
    // user asked for.
    const updateTyped = props.mixedTypes ? props.onUpdatePrimary : props.onUpdate

    const ownerLabel = ownerName(element.owner) || roomFallbackLabel || element.owner.assetName || ''
    const ownerType = element.owner.assetTypeCode ?? element.owner.newAsset?.typeCode ?? ''
    // The list is of **layers**, not footprints: two drawings of one asset on
    // the same layer are a duplicate (a validation warning), not sharing. So
    // rows are deduplicated by frame, and "shared" means more than one frame.
    const layerRows = props.sharedLayers.reduce<{ frameUid: string; frameName: string; current: boolean; count: number }[]>(
        (rows, layer) => {
            const existing = rows.find((r) => r.frameUid === layer.frameUid)
            if (existing) existing.count += 1
            else rows.push({ frameUid: layer.frameUid, frameName: layer.frameName, current: layer.current, count: 1 })
            return rows
        },
        [],
    )
    const shared = layerRows.length > 1

    return (
        <div className="flex flex-col gap-1 p-3" data-testid="plan-editor-inspector">
            <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    {/* A grouper is a space in the geometry and a lot on the
                      * plan: naming it "Room" here would be the one place the
                      * editor lies about what is selected. */}
                    {isGrouperElement(element)
                        ? props.typeLabelOf(element.owner.newAsset?.typeCode ?? element.owner.assetTypeCode ?? 'unit')
                        : t(`planEditor.kinds.${element.kind}`)}
                    {props.selectionCount > 1 && (
                        <span
                            className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground"
                            data-testid="plan-editor-selection-count"
                            title={t('planEditor.selection_multi_hint')}
                        >
                            {t('planEditor.selection_multi', { count: props.selectionCount })}
                        </span>
                    )}
                    {/* §5a: the element is flagged where the user is looking at
                      * it, not only in the panel at the bottom. */}
                    {(props.elementIssues?.length ?? 0) > 0 && (
                        <span
                            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                props.elementIssues?.some((i) => i.severity === 'error')
                                    ? 'bg-destructive/15 text-destructive'
                                    : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                            }`}
                            data-testid="plan-editor-issue-badge"
                            title={t('planEditor.floorplanIssues.badge_hint')}
                        >
                            <AppIcon path={mdiAlertCircleOutline} size="xs" />
                            {props.elementIssues?.length}
                        </span>
                    )}
                </h2>
                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="icon"
                        aria-label={t('planEditor.duplicate')}
                        title={`${t('planEditor.duplicate')} (Ctrl+D)`}
                        data-testid="plan-editor-inspector-duplicate"
                        onClick={props.onDuplicate}
                    >
                        <AppIcon path={mdiContentDuplicate} size="sm" />
                    </Button>
                    <Button
                        variant="destructive"
                        size="icon"
                        aria-label={t('planEditor.delete_element')}
                        title={t(shared ? 'planEditor.delete_footprint_hint' : 'planEditor.delete_element_hint')}
                        data-testid="plan-editor-inspector-delete"
                        onClick={props.onDelete}
                    >
                        <AppIcon path={mdiTrashCanOutline} size="sm" />
                    </Button>
                </div>
            </div>

            {element.kind === 'run' && (() => {
                // A run depicts a network asset: linked, or the one its level
                // parent has or will get on save (spec 2026-10-20-33).
                const linked = element.owner.assetUid && element.owner.assetUid !== element.source.repOwner?.assetUid
                    ? element.owner
                    : null
                const target = linked ? undefined : props.runTargetOf?.(element)
                const labelOf = props.networkLabelOf ?? ((c: string) => c)
                // Changing the network or the level moves the run to the
                // matching asset: the link goes, and the save resolves it again.
                const unlink = (el: EditorElement) => {
                    if (el.kind === 'run') el.owner = { ...(el.source.repOwner ?? {}) }
                }
                return (
                    <div className="rounded-md border border-border p-2" data-testid="plan-editor-run-fields">
                        <div className="flex items-center gap-2 py-1">
                            <Label htmlFor="plan-editor-field-network" className="w-24 shrink-0 text-xs text-muted-foreground">
                                {t('planEditor.field_network')}
                            </Label>
                            <NetworkSelect
                                id="plan-editor-field-network"
                                testId="plan-editor-field-network"
                                networks={props.networks ?? []}
                                value={element.network}
                                onChange={(code) => props.onUpdate((el) => {
                                    if (el.kind !== 'run' || el.network === code) return
                                    el.network = code
                                    unlink(el)
                                })}
                                labelOf={labelOf}
                            />
                        </div>
                        <div className="flex items-center gap-2 py-1">
                            <span className="w-24 shrink-0 text-xs text-muted-foreground">{t('planEditor.field_run_level')}</span>
                            <RunLevelSwitch
                                testId="plan-editor-field-run-level"
                                value={props.runLevelOf?.(element)}
                                parents={props.runLevelParents ?? {}}
                                onChange={(level) => {
                                    props.onRunLevelPicked?.(element.network, level)
                                    props.onUpdate((el) => {
                                        if (el.kind !== 'run') return
                                        if (props.runLevelOf?.(el) === level) return
                                        el.level = level
                                        unlink(el)
                                    })
                                }}
                            />
                        </div>
                        <div className="flex items-center gap-2 py-1">
                            <Label htmlFor="plan-editor-field-run-width" className="w-24 shrink-0 text-xs text-muted-foreground">
                                {t('planEditor.field_run_width')}
                            </Label>
                            <RunWidthField
                                id="plan-editor-field-run-width"
                                testId="plan-editor-field-run-width"
                                value={element.widthMm ?? null}
                                onCommit={(mm) => props.onUpdate((el) => {
                                    if (el.kind !== 'run') return
                                    if (mm) el.widthMm = mm
                                    else delete el.widthMm
                                })}
                            />
                        </div>
                        <div className="flex items-center gap-2 py-1 text-xs">
                            <span className="w-24 shrink-0 text-muted-foreground">{t('planEditor.field_length')}</span>
                            <span className="text-foreground" data-testid="plan-editor-run-length">
                                {formatLengthMm(runLength(element), props.mmPerUnit)}
                            </span>
                            {props.mmPerUnit === null && <UncalibratedBadge />}
                        </div>

                        <div className="mt-2 border-t border-border pt-1">
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                {t('planEditor.run_network_asset')}
                            </div>
                            {linked ? (
                                <div className="flex items-center gap-1">
                                    <Link
                                        to="/$org/assets/$assetUid"
                                        params={{ org: props.org, assetUid: linked.assetUid as string }}
                                        className="truncate text-xs text-primary hover:underline"
                                        data-testid="plan-editor-run-asset-link"
                                    >
                                        {linked.assetName || linked.assetUid}
                                    </Link>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="ml-auto h-6 px-1 text-[11px]"
                                        data-testid="plan-editor-run-unlink"
                                        onClick={() => props.onUpdate(unlink)}
                                    >
                                        {t('planEditor.run_unlink')}
                                    </Button>
                                </div>
                            ) : target?.kind === 'existing' ? (
                                <div className="flex items-center gap-1">
                                    <Link
                                        to="/$org/assets/$assetUid"
                                        params={{ org: props.org, assetUid: target.asset.uid }}
                                        className="truncate text-xs text-primary hover:underline"
                                        data-testid="plan-editor-run-asset-link"
                                    >
                                        {target.asset.name}
                                    </Link>
                                    <span className="ml-auto shrink-0 text-[11px] text-muted-foreground" data-testid="plan-editor-run-asset-reused">
                                        {t('planEditor.run_network_asset_linked_on_save')}
                                    </span>
                                </div>
                            ) : target?.kind === 'pending' ? (
                                <div className="text-xs" data-testid="plan-editor-run-asset-new">
                                    <span className="text-foreground" data-testid="plan-editor-run-asset-new-name">{target.pending.name}</span>
                                    <span className="block text-[11px] text-muted-foreground">{t('planEditor.run_network_asset_new')}</span>
                                </div>
                            ) : (
                                <div className="text-xs text-muted-foreground" data-testid="plan-editor-run-asset-unavailable">
                                    {t('planEditor.run_network_asset_unavailable')}
                                </div>
                            )}
                            {linkingRun ? (
                                <div className="pt-1">
                                    <AssetScopeChip
                                        scopeLabel={props.networkScopeLabel ?? props.scopeLabel}
                                        widened={widened || !(props.networkScopeUid ?? props.scopeUid)}
                                        onWiden={() => setWidened(true)}
                                        testId="plan-editor-run-link"
                                    />
                                    <ApiSearchableSelect
                                        key={`${networkScopeUid ?? 'everywhere'}-run`}
                                        placeholder={t('planEditor.run_link_placeholder')}
                                        searchPlaceholder={t('planEditor.run_link_placeholder')}
                                        onSearch={searchNetworkAssets}
                                        minSearchLength={0}
                                        contentClassName={WIDE_ASSET_MENU}
                                        contentAlign="end"
                                        onOptionSelect={(option) => {
                                            const data = option.data as { fullLabel?: string; asset_type_code?: string } | undefined
                                            const name = data?.fullLabel ?? option.label
                                            // Every selected run: linking a whole branch of
                                            // ductwork to its network is one gesture.
                                            props.onUpdate((el) => {
                                                if (el.kind !== 'run') return
                                                el.owner = {
                                                    assetUid: option.value,
                                                    assetName: name,
                                                    ...(data?.asset_type_code ? { assetTypeCode: data.asset_type_code } : {}),
                                                }
                                            })
                                            setLinkingRun(false)
                                        }}
                                        data-testid="plan-editor-run-link-select"
                                    />
                                </div>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-1 h-6 px-1 text-[11px]"
                                    data-testid="plan-editor-run-link"
                                    onClick={() => setLinkingRun(true)}
                                >
                                    <AppIcon path={mdiLinkVariant} size="sm" className="mr-1" />
                                    {t('planEditor.run_link')}
                                </Button>
                            )}
                        </div>

                        {props.runLevelOf?.(element) === 'floor' && (() => {
                            // A floor network is a segment of its building's
                            // (spec 2026-10-20-35): Part of, or what the save
                            // will attach it to.
                            const segmentUid = linked?.assetUid ?? (target?.kind === 'existing' ? target.asset.uid : undefined)
                            if (segmentUid) {
                                return (
                                    <div className="mt-2 border-t border-border pt-1" data-testid="plan-editor-run-network-assembly-field">
                                        <NetworkAssemblyControl
                                            org={props.org}
                                            assetUid={segmentUid}
                                            selectTestId="plan-editor-run-network-assembly"
                                            promoteTestId="plan-editor-run-move-to-building"
                                            promoteName={props.runAssemblyNameOf?.(element)}
                                            onChanged={props.onNetworkAssemblyChanged}
                                        />
                                    </div>
                                )
                            }
                            const pending = target?.kind === 'pending' ? props.runSegmentAssemblyOf?.(element) : undefined
                            if (!pending || pending.kind === 'none') return null
                            return (
                                <div className="mt-2 border-t border-border pt-1 text-[11px] text-muted-foreground" data-testid="plan-editor-run-network-assembly-pending">
                                    {pending.kind === 'one'
                                        ? t('network_segments.will_join', { name: pending.asset.name })
                                        : t('network_segments.choose_after_save')}
                                </div>
                            )
                        })()}

                        {(props.elementIssues?.length ?? 0) > 0 && (
                            <ul className="mt-1 space-y-0.5" data-testid="plan-editor-element-issues">
                                {props.elementIssues?.map((issue, i) => (
                                    <li key={`${issue.code}-${i}`} className="flex items-start gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                                        <AppIcon path={mdiAlertCircleOutline} size="xs" className="mt-0.5 shrink-0" />
                                        <span>{t(`planEditor.issues.${issue.code}`, { ...(issue.params ?? {}) }) as string}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )
            })()}

            {element.kind === 'riser' && (() => {
                const linked = element.owner.assetUid && element.owner.assetUid !== element.source.repOwner?.assetUid
                    ? element.owner
                    : null
                const alignment = linked?.assetUid ? props.riserAlignmentFor?.(linked.assetUid) : undefined
                const aligned = alignment
                    && Math.hypot(alignment.point.x - element.position.x, alignment.point.y - element.position.y) < 1e-6
                return (
                    <div className="rounded-md border border-border p-2" data-testid="plan-editor-riser-fields">
                        <div className="flex items-center gap-2 py-1">
                            <Label htmlFor="plan-editor-field-riser-network" className="w-24 shrink-0 text-xs text-muted-foreground">
                                {t('planEditor.field_network')}
                            </Label>
                            <NetworkSelect
                                id="plan-editor-field-riser-network"
                                testId="plan-editor-field-riser-network"
                                networks={props.networks ?? []}
                                value={element.network}
                                onChange={(code) => props.onUpdate((el) => {
                                    if (el.kind === 'riser') el.network = code
                                })}
                                labelOf={props.networkLabelOf ?? ((c: string) => c)}
                            />
                        </div>
                        <div className="flex items-center gap-2 py-1">
                            <Label htmlFor="plan-editor-field-riser-diameter" className="w-24 shrink-0 text-xs text-muted-foreground">
                                {t('planEditor.field_riser_diameter')}
                            </Label>
                            <RunWidthField
                                id="plan-editor-field-riser-diameter"
                                testId="plan-editor-field-riser-diameter"
                                value={element.diameterMm ?? null}
                                onCommit={(mm) => props.onUpdate((el) => {
                                    if (el.kind !== 'riser') return
                                    if (mm) el.diameterMm = mm
                                    else delete el.diameterMm
                                })}
                            />
                        </div>
                        <div className="flex items-center gap-2 py-1">
                            <Label htmlFor="plan-editor-field-riser-passage" className="w-24 shrink-0 text-xs text-muted-foreground">
                                {t('planEditor.field_riser_passage')}
                            </Label>
                            <RiserPassageSelect
                                id="plan-editor-field-riser-passage"
                                testId="plan-editor-field-riser-passage"
                                value={element.passage}
                                onChange={(passage) => props.onUpdate((el) => {
                                    if (el.kind !== 'riser') return
                                    if (passage) el.passage = passage
                                    else delete el.passage
                                })}
                            />
                        </div>
                        <div className="flex items-center gap-2 py-1">
                            <Label htmlFor="plan-editor-field-riser-label" className="w-24 shrink-0 text-xs text-muted-foreground">
                                {t('planEditor.field_riser_label')}
                            </Label>
                            <Input
                                id="plan-editor-field-riser-label"
                                data-testid="plan-editor-field-riser-label"
                                className="h-7 text-xs"
                                defaultValue={element.label ?? ''}
                                key={`${element.key}-label`}
                                onBlur={(e) => {
                                    const label = e.target.value.trim()
                                    props.onUpdate((el) => {
                                        if (el.kind !== 'riser' || (el.label ?? '') === label) return
                                        if (label) el.label = label
                                        else delete el.label
                                    })
                                }}
                            />
                        </div>

                        <div className="mt-2 border-t border-border pt-1">
                            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                {t('planEditor.riser_column')}
                            </div>
                            {linked ? (
                                <div className="flex items-center gap-1">
                                    <Link
                                        to="/$org/assets/$assetUid"
                                        params={{ org: props.org, assetUid: linked.assetUid as string }}
                                        className="truncate text-xs text-primary hover:underline"
                                        data-testid="plan-editor-riser-asset-link"
                                    >
                                        {linked.assetName || linked.assetUid}
                                    </Link>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="ml-auto h-6 px-1 text-[11px]"
                                        data-testid="plan-editor-riser-unlink"
                                        onClick={() => props.onUpdate((el) => {
                                            if (el.kind === 'riser') el.owner = { ...(el.source.repOwner ?? {}) }
                                        })}
                                    >
                                        {t('planEditor.run_unlink')}
                                    </Button>
                                </div>
                            ) : (
                                <div className="text-xs text-muted-foreground" data-testid="plan-editor-riser-unlinked">
                                    {t('planEditor.riser_column_none')}
                                </div>
                            )}
                            {alignment && !aligned && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-1 h-6 px-2 text-[11px]"
                                    data-testid="plan-editor-riser-align"
                                    onClick={() => {
                                        props.onUpdate((el) => {
                                            if (el.kind === 'riser') el.position = { ...alignment.point }
                                        })
                                        setRiserAlignedOn(alignment.levelName)
                                    }}
                                >
                                    {t('planEditor.riser_align', { level: alignment.levelName })}
                                </Button>
                            )}
                            {riserAlignedOn && aligned && (
                                <p className="mt-1 text-[11px] text-muted-foreground" data-testid="plan-editor-riser-aligned">
                                    {t('planEditor.riser_aligned', { level: riserAlignedOn })}
                                </p>
                            )}
                            {linkingRun ? (
                                <div className="pt-1">
                                    <AssetScopeChip
                                        scopeLabel={props.scopeLabel}
                                        widened={widened || !props.scopeUid}
                                        onWiden={() => setWidened(true)}
                                        testId="plan-editor-riser-link"
                                    />
                                    <ApiSearchableSelect
                                        key={`${scopeUid ?? 'everywhere'}-riser`}
                                        placeholder={t('planEditor.riser_link_placeholder')}
                                        searchPlaceholder={t('planEditor.riser_link_placeholder')}
                                        onSearch={searchAssets}
                                        minSearchLength={0}
                                        contentClassName={WIDE_ASSET_MENU}
                                        contentAlign="end"
                                        onOptionSelect={(option) => {
                                            const data = option.data as { fullLabel?: string } | undefined
                                            // The column's position on the other floors
                                            // pre-fills this one: columns are vertical.
                                            const target = props.riserAlignmentFor?.(option.value)
                                            props.onUpdate((el) => {
                                                if (el.kind !== 'riser') return
                                                el.owner = { assetUid: option.value, assetName: data?.fullLabel ?? option.label }
                                                if (target) el.position = { ...target.point }
                                            })
                                            setRiserAlignedOn(target?.levelName ?? null)
                                            setLinkingRun(false)
                                        }}
                                        data-testid="plan-editor-riser-link-select"
                                    />
                                </div>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="mt-1 h-6 px-1 text-[11px]"
                                    data-testid="plan-editor-riser-link"
                                    onClick={() => setLinkingRun(true)}
                                >
                                    <AppIcon path={mdiLinkVariant} size="sm" className="mr-1" />
                                    {t('planEditor.riser_link')}
                                </Button>
                            )}
                        </div>
                    </div>
                )
            })()}

            {/* Owning asset — navigate to it, or attach the geometry elsewhere. */}
            {element.kind !== 'run' && element.kind !== 'riser' && (
            <div className="rounded-md border border-border p-2">
                <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t('planEditor.owning_asset')}
                    {shared && (
                        <span
                            className="inline-flex items-center gap-1 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-primary"
                            data-testid="plan-editor-shared-badge"
                            title={t('planEditor.shared_hint')}
                        >
                            <AppIcon path={mdiLayersTriple} size="xs" />
                            {t('planEditor.shared_badge', {
                                count: layerRows.length,
                                parent: props.parent?.name ?? t('planEditor.owner_none'),
                            })}
                        </span>
                    )}
                </div>
                {element.owner.assetUid ? (
                    <Link
                        to="/$org/assets/$assetUid"
                        params={{ org: props.org, assetUid: element.owner.assetUid }}
                        className="text-xs text-primary hover:underline"
                        data-testid="plan-editor-owner-link"
                    >
                        {ownerLabel || element.owner.assetUid}
                    </Link>
                ) : element.owner.newAsset ? (
                    <div className="text-xs text-foreground" data-testid="plan-editor-owner-pending">
                        {t('planEditor.owner_pending', { name: ownerLabel || t('planEditor.untitled'), type: ownerType })}
                    </div>
                ) : (
                    <div className="text-xs text-muted-foreground" data-testid="plan-editor-owner-none">
                        {t('planEditor.owner_none')}
                    </div>
                )}
                {linking ? (
                    <div className="pt-1">
                        <AssetScopeChip
                            scopeLabel={props.scopeLabel}
                            widened={widened || !props.scopeUid}
                            onWiden={() => setWidened(true)}
                            testId="plan-editor-link"
                        />
                        <ApiSearchableSelect
                            key={scopeUid ?? 'everywhere'}
                            placeholder={t('planEditor.link_existing_placeholder')}
                            searchPlaceholder={t('planEditor.link_existing_placeholder')}
                            onSearch={searchAssets}
                            minSearchLength={0}
                            // The inspector is 18rem wide: a menu that copies it
                            // cuts apartment names in half. Open wider, growing
                            // leftwards into the canvas.
                            contentClassName={WIDE_ASSET_MENU}
                            contentAlign="end"
                            onOptionSelect={(option) => {
                                props.onLinkAsset(
                                    option.value,
                                    (option.data as { fullLabel?: string } | undefined)?.fullLabel ?? option.label,
                                    option.data as { asset_type_uid?: string; asset_type_code?: string; parent_asset_uid?: string } | undefined,
                                )
                                setLinking(false)
                            }}
                            data-testid="plan-editor-link-asset-select"
                        />
                    </div>
                ) : (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mt-1 h-6 px-1 text-[11px]"
                        data-testid="plan-editor-link-asset"
                        onClick={() => setLinking(true)}
                    >
                        <AppIcon path={mdiLinkVariant} size="sm" className="mr-1" />
                        {t('planEditor.link_existing')}
                    </Button>
                )}

                {/*
                  * Promotion and demotion move *ownership*, never geometry
                  * (spec 2026-08-22-03 §3/§4): promoting extracts the element
                  * into its own representation on its own asset, demoting folds
                  * it back into a bundle while keeping the asset it depicts.
                  */}
                {props.promotable && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-1 h-6 w-full px-1 text-[11px]"
                        title={t('planEditor.promote_hint')}
                        data-testid="plan-editor-promote"
                        onClick={props.onPromote}
                    >
                        <AppIcon path={mdiCallSplit} size="sm" className="mr-1" />
                        {t('planEditor.promote')}
                    </Button>
                )}

                {props.promotable && (props.promoteAllCount ?? 0) > 1 && props.onPromoteAll && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mt-1 h-6 w-full px-1 text-[11px]"
                        // §5a: promoting a bundle with an error-level issue
                        // would write the bad geometry into the inventory in
                        // one click. The individual Promote stays available —
                        // one room at a time is a review, not a rubber stamp.
                        title={props.promoteAllBlocked
                            ? t('planEditor.floorplanIssues.promote_all_blocked')
                            : t('planEditor.promoteAll.hint')}
                        disabled={props.promoteAllBlocked}
                        data-testid="plan-editor-promote-all"
                        onClick={props.onPromoteAll}
                    >
                        <AppIcon path={mdiCallSplit} size="xs" className="mr-1" />
                        {t('planEditor.promoteAll.action')} ({props.promoteAllCount})
                    </Button>
                )}

                {(props.elementIssues?.length ?? 0) > 0 && (
                    <ul className="mt-1 space-y-0.5" data-testid="plan-editor-element-issues">
                        {props.elementIssues?.map((issue, i) => (
                            <li
                                key={`${issue.code}-${i}`}
                                className={`flex items-start gap-1 text-[11px] ${
                                    issue.severity === 'error'
                                        ? 'text-destructive'
                                        : 'text-amber-600 dark:text-amber-400'
                                }`}
                            >
                                <AppIcon path={mdiAlertCircleOutline} size="xs" className="mt-0.5 shrink-0" />
                                <span>{t(`planEditor.issues.${issue.code}`, { ...(issue.params ?? {}) }) as string}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {props.provenance && (
                    <p
                        className="pt-2 text-[11px] italic text-muted-foreground"
                        data-testid="plan-editor-provenance"
                    >
                        {props.provenance}
                    </p>
                )}

                {!props.promotable && props.demoteTargets.length > 0 && (
                    merging ? (
                        <div className="flex items-center gap-1 pt-1">
                            <select
                                className="h-7 flex-1 rounded-md border border-input bg-background px-1 text-[11px] text-foreground"
                                defaultValue=""
                                aria-label={t('planEditor.demote')}
                                data-testid="plan-editor-demote-target"
                                onChange={(e) => {
                                    if (!e.target.value) return
                                    props.onDemote(e.target.value)
                                    setMerging(false)
                                }}
                            >
                                <option value="">{t('planEditor.demote_pick')}</option>
                                {props.demoteTargets.map((target) => (
                                    <option key={target.repUid} value={target.repUid}>
                                        {target.label} ({target.elementCount})
                                    </option>
                                ))}
                            </select>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-1 text-[11px]"
                                data-testid="plan-editor-demote-cancel"
                                onClick={() => setMerging(false)}
                            >
                                {t('planEditor.cancel')}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            className="mt-1 h-6 w-full px-1 text-[11px]"
                            title={t('planEditor.demote_hint')}
                            data-testid="plan-editor-demote"
                            onClick={() => setMerging(true)}
                        >
                            <AppIcon path={mdiSetMerge} size="xs" className="mr-1" />
                            {t('planEditor.demote')}
                        </Button>
                    )
                )}

                {props.sharedLoading && !shared && element.owner.assetUid && (
                    <p
                        className="pt-1 text-[11px] text-muted-foreground"
                        data-testid="plan-editor-shared-loading"
                    >
                        {t('planEditor.shared_resolving')}
                    </p>
                )}

                {/*
                  * Every layer this asset is drawn on (§5). Deleting the
                  * element here removes **this** footprint only — the asset,
                  * and its drawings elsewhere, survive.
                  */}
                {shared && (
                    <div className="mt-2 border-t border-border pt-1">
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {t('planEditor.shared_layers')}
                        </div>
                        <ul className="pt-1">
                            {layerRows.map((layer) => (
                                <li key={layer.frameUid}>
                                    <button
                                        type="button"
                                        className={`w-full rounded px-1 py-0.5 text-left text-[11px] hover:bg-muted ${layer.current ? 'font-medium text-foreground' : 'text-primary'}`}
                                        data-testid={`plan-editor-shared-layer-${layer.frameUid}`}
                                        disabled={layer.current}
                                        onClick={() => props.onOpenLayer(layer.frameUid)}
                                    >
                                        {layer.frameName}
                                        {layer.current ? ` — ${t('planEditor.shared_layer_current')}` : ''}
                                        {layer.count > 1 ? ` (${layer.count})` : ''}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <p className="pt-1 text-[11px] text-muted-foreground" data-testid="plan-editor-shared-delete-hint">
                            {t('planEditor.shared_delete_hint')}
                        </p>
                    </div>
                )}

                {/* The resolved parent (§2) — shown, and overridable before save. */}
                <div className="mt-2 border-t border-border pt-1">
                    <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                        {t('planEditor.resolved_parent')}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            aria-label={t('planEditor.parent_override')}
                            title={t('planEditor.parent_override')}
                            data-testid="plan-editor-parent-override"
                            onClick={() => setReparenting((v) => !v)}
                        >
                            <AppIcon path={mdiPencilOutline} size="xs" />
                        </Button>
                    </div>
                    <div className="text-xs text-foreground" data-testid="plan-editor-parent-value">
                        {props.parent?.name || t('planEditor.owner_none')}
                    </div>
                    {/*
                      * Offered right after a drop, and never later: the answer
                      * is obvious while the move is still on screen.
                      */}
                    {props.parentSuggestion && (
                        <div
                            className="mt-1 flex flex-wrap items-center gap-1 rounded border border-primary/40 bg-primary/10 px-1.5 py-1"
                            data-testid="plan-editor-parent-suggestion"
                        >
                            <span className="text-[11px] text-foreground">
                                {t('planEditor.parent_suggestion', { name: props.parentSuggestion.name })}
                            </span>
                            <Button
                                variant="default"
                                size="sm"
                                className="h-5 px-1.5 text-[11px]"
                                data-testid="plan-editor-parent-suggestion-accept"
                                onClick={() => {
                                    // Re-parenting an asset that already exists
                                    // moves its implicit frame, so it waits
                                    // behind the same confirm as the manual
                                    // override (§6). A pending one is local.
                                    const uid = props.parentSuggestion?.parentUid
                                    if (element.owner.assetUid && uid) {
                                        setPendingReparent({
                                            uid,
                                            name: props.parentSuggestion!.name,
                                            intent: 'parent',
                                        })
                                    } else {
                                        props.onAcceptParentSuggestion()
                                    }
                                }}
                            >
                                {t('planEditor.parent_suggestion_accept')}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 px-1.5 text-[11px]"
                                data-testid="plan-editor-parent-suggestion-dismiss"
                                onClick={props.onDismissParentSuggestion}
                            >
                                {t('planEditor.parent_suggestion_dismiss')}
                            </Button>
                        </div>
                    )}
                    {reparenting && (
                        <div className="pt-1">
                            <AssetScopeChip
                                scopeLabel={props.scopeLabel}
                                widened={widened || !props.scopeUid}
                                onWiden={() => setWidened(true)}
                                testId="plan-editor-reparent"
                            />
                            <ApiSearchableSelect
                                key={scopeUid ?? 'everywhere'}
                                placeholder={t('planEditor.parent_override_placeholder')}
                                searchPlaceholder={t('planEditor.parent_override_placeholder')}
                                onSearch={searchAssets}
                                minSearchLength={0}
                                contentClassName={WIDE_ASSET_MENU}
                                contentAlign="end"
                                onOptionSelect={(option) => {
                                    setReparenting(false)
                                    if (element.owner.assetUid) setPendingReparent({ uid: option.value, name: (option.data as { fullLabel?: string } | undefined)?.fullLabel ?? option.label, intent: 'parent' })
                                    else props.onParentChange(option.value, (option.data as { fullLabel?: string } | undefined)?.fullLabel ?? option.label)
                                }}
                                data-testid="plan-editor-parent-override-select"
                            />
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* §6 — the reparent guard. */}
            <AlertDialog open={pendingReparent !== null}>
                <AlertDialogContent data-testid="plan-editor-reparent-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t(pendingReparent?.intent === 'wall_owner'
                                ? 'planEditor.reparent_wall_title'
                                : 'planEditor.reparent_title')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {/* A group re-parents together, so the confirm has
                              * to say how many are moving, not name one. */}
                            {t(pendingReparent?.intent === 'wall_owner'
                                ? 'planEditor.reparent_wall_description'
                                : props.selectionCount > 1
                                    ? 'planEditor.reparent_description_multi'
                                    : 'planEditor.reparent_description', {
                                name: ownerLabel || element.owner.assetUid || t('planEditor.untitled'),
                                parent: pendingReparent?.name ?? '',
                                count: props.selectionCount,
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="plan-editor-reparent-cancel"
                            onClick={() => setPendingReparent(null)}
                        >
                            {t('planEditor.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="plan-editor-reparent-apply"
                            onClick={() => {
                                if (pendingReparent?.intent === 'wall_owner') props.onRetargetWall(pendingReparent.uid)
                                else if (pendingReparent) props.onParentChange(pendingReparent.uid, pendingReparent.name)
                                setPendingReparent(null)
                            }}
                        >
                            {t('planEditor.reparent_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {element.kind === 'wall' && props.wallOwners.length > 1 && (
                // §5: re-targeting a wall is a cross-representation cut/paste in
                // the same frame — the openings drawn on it follow.
                // Re-owning a wall bundle across frame anchors is guarded like
                // any other frame-moving act (spec 2026-08-25-04 §6).
                <SelectField
                    id="plan-editor-wall-owner"
                    label={t('planEditor.wall_owner')}
                    value={element.owner.assetUid ?? ''}
                    options={props.wallOwners.map((o) => ({ value: o.uid, label: o.name }))}
                    onChange={(uid) => {
                        const target = props.wallOwners.find((o) => o.uid === uid)
                        if (target?.crossFrame) setPendingReparent({ uid, name: target.name, intent: 'wall_owner' })
                        else props.onRetargetWall(uid)
                    }}
                />
            )}

            {element.kind === 'wall' && (
                <div className="flex items-center gap-2 py-1 text-xs">
                    <span className="w-28 shrink-0 text-muted-foreground">{t('planEditor.field_length')}</span>
                    <span className="text-foreground" data-testid="plan-editor-field-length">
                        {formatLengthMm(dist(element.start, element.end), props.mmPerUnit)}
                    </span>
                    {props.mmPerUnit === null && <UncalibratedBadge />}
                </div>
            )}

            {element.kind === 'wall' && (
                <>
                    {/*
                      * Not a plain `onUpdate`: a thickness set here is also the
                      * thickness the next wall is drawn with, so the page routes
                      * it through the session memory the wall tool reads — and
                      * the same recent values are one click away, because
                      * matching an existing wall is the usual reason to edit one.
                      */}
                    <NumberField
                        id="plan-editor-field-thickness"
                        label={t('planEditor.field_thickness')}
                        value={Math.round(props.toMm(element.thickness))}
                        step={10}
                        min={10}
                        onCommit={props.onWallThickness}
                    />
                    <div className="flex gap-2 pb-1">
                        <span className="w-28 shrink-0" aria-hidden />
                        <WallThicknessChips
                            recent={props.recentThicknesses}
                            active={Math.round(props.toMm(element.thickness))}
                            onPick={props.onWallThickness}
                        />
                    </div>
                </>
            )}

            {element.kind === 'space' && (
                <>
                    <div className="flex items-center gap-2 py-1">
                        <Label htmlFor="plan-editor-field-room-name" className="w-28 shrink-0 text-xs text-muted-foreground">
                            {t('planEditor.field_name')}
                        </Label>
                        <Input
                            ref={nameInputRef}
                            id="plan-editor-field-room-name"
                            data-testid="plan-editor-field-room-name"
                            className="h-7 text-xs"
                            // The **asset's** name, not the drawing's. A
                            // representation carries a label of its own — an
                            // imported floorplan calls this space `T5-3171`
                            // while the asset it depicts is `E17-T5-2` — and
                            // showing that label here made the field contradict
                            // the owning asset named two lines above it. What
                            // this panel edits is the asset (spec
                            // 2026-08-30-03 §12); the drawing's own label
                            // follows so the plan cannot disagree with it.
                            value={element.owner.newAsset
                                ? element.owner.newAsset.name
                                : element.owner.assetNameEdit
                                    // An auto-named room shows an *empty* field
                                    // over its derivation, exactly like a pending
                                    // one: what is stored is the server's, and
                                    // typing here is what takes it over. Clearing
                                    // a typed name hands it back.
                                    ?? (element.owner.assetAutoName ? '' : element.owner.assetName ?? element.name)}
                            // A room needs no name (spec 2026-08-31-02 §2): the
                            // placeholder shows what it reads as while the field
                            // is empty — "Chambre 2" — so leaving it alone is
                            // visibly a choice rather than an unfinished form.
                            placeholder={roomFallbackLabel || t('planEditor.field_name_placeholder')}
                            onChange={(e) => props.onUpdate((el) => {
                                if (el.kind !== 'space') return
                                if (el.owner.newAsset) {
                                    // Nothing exists yet: the pending asset
                                    // takes the name, and the shape it is
                                    // drawn as carries the same label.
                                    el.owner.newAsset.name = e.target.value
                                    el.name = e.target.value
                                } else {
                                    // The asset is the thing being renamed. The
                                    // representation's own label is left alone
                                    // — rewriting it would be a second, silent
                                    // edit to a different object, and the plan
                                    // already reads the asset's name.
                                    el.owner.assetNameEdit = e.target.value
                                }
                            })}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') (e.target as HTMLInputElement).blur()
                                if (e.key === 'Enter') {
                                    (e.target as HTMLInputElement).blur()
                                    // Enter writes the name **when the field was
                                    // opened to rename** — double-click, type,
                                    // Enter, done (spec 2026-08-30-03 §13).
                                    // While *drawing*, Enter is what ends the
                                    // shape and the name is one field of
                                    // something still being built; saving there
                                    // would write half a floor because a room
                                    // was given a name.
                                    if (renaming) {
                                        setRenaming(false)
                                        props.onSaveRequested?.()
                                    }
                                }
                            }}
                        />
                    </div>
                    <div className="flex items-center gap-2 py-1 text-xs">
                        <span className="w-28 shrink-0 text-muted-foreground">{t('planEditor.field_area')}</span>
                        <span className="text-foreground" data-testid="plan-editor-field-area">
                            {formatAreaM2(spaceArea(element), props.mmPerUnit)}
                        </span>
                        {props.mmPerUnit === null && <UncalibratedBadge />}
                    </div>
                    {/*
                      * The holes the area above is net of (spec 2026-10-19-07),
                      * one row each: a pillar traced by mistake goes from here,
                      * without hunting for one of its corners on the plan.
                      */}
                    {spaceHoles(element).length > 0 && (
                        <div className="flex flex-col gap-0.5 py-1 text-xs" data-testid="plan-editor-holes">
                            <span className="text-muted-foreground">
                                {t('planEditor.holes_title', { count: spaceHoles(element).length })}
                            </span>
                            {spaceHoles(element).map((hole, i) => (
                                <div key={i} className="flex items-center gap-2 pl-2" data-testid="plan-editor-hole-row">
                                    <span className="flex-1 text-foreground">
                                        {t('planEditor.hole_row', { index: i + 1, area: formatAreaM2(shoelace(hole), props.mmPerUnit) })}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        aria-label={t('planEditor.hole_remove', { index: i + 1 })}
                                        title={t('planEditor.hole_remove', { index: i + 1 })}
                                        data-testid={`plan-editor-hole-remove-${i}`}
                                        onClick={() => props.onUpdatePrimary((el) => {
                                            if (el.kind === 'space') removeHole(el, i)
                                        })}
                                    >
                                        <AppIcon path={mdiTrashCanOutline} size="xs" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    {isGrouperElement(element) ? (
                        // A grouper decides what it *is*, not what it belongs
                        // to — and unlike an opening's type, it stays editable
                        // after the save (spec 2026-08-30-03 §2). A lot typed
                        // wrong on the first pass is a thing the person drawing
                        // the floor can see and should be able to fix, without
                        // leaving for the asset page.
                        <>
                            <SelectField
                                id="plan-editor-field-grouper-kind"
                                label={t('planEditor.field_kind')}
                                value={grouperKindOf(
                                    element.owner.newAsset?.typeCode ?? element.owner.assetTypeCode,
                                    element.owner.newAsset
                                        ? element.owner.newAsset.unitKind
                                        // An untouched saved grouper shows what
                                        // is stored; the owner carries only what
                                        // this session changed (spec §4).
                                        : element.owner.assetUnitKind ?? storedGrouper?.unitKind,
                                )}
                                options={GROUPER_KINDS.map((k) => ({
                                    value: k.id,
                                    label: t(`planEditor.grouper_kind.${k.id}`),
                                }))}
                                onChange={(id) => props.onUpdate((el) => {
                                    if (el.kind !== 'space') return
                                    const { typeCode, unitKind } = grouperKindSpec(id as GrouperKindId)
                                    if (el.owner.newAsset) {
                                        el.owner.newAsset.typeCode = typeCode
                                        if (unitKind) el.owner.newAsset.unitKind = unitKind
                                        else delete el.owner.newAsset.unitKind
                                    } else {
                                        el.owner.assetTypeCode = typeCode
                                        if (unitKind) el.owner.assetUnitKind = unitKind
                                        else delete el.owner.assetUnitKind
                                    }
                                    // Re-derive the cross-layer flag: only an
                                    // anchor type needs the registration row.
                                    // Turning a drawn unit into a common area
                                    // and leaving the flag set would write an
                                    // identity registration for geometry that
                                    // already resolves to this floor on its own
                                    // (spec 2026-08-30-01 §7).
                                    if (typeAnchorsFrame(typeCode)) el.owner.crossLayer = true
                                    else delete el.owner.crossLayer
                                })}
                            />
                            <div className="flex items-center gap-2 py-1">
                                <Label
                                    htmlFor="plan-editor-field-grouper-code"
                                    className="w-28 shrink-0 text-xs text-muted-foreground"
                                >
                                    {t('planEditor.field_code')}
                                </Label>
                                <Input
                                    id="plan-editor-field-grouper-code"
                                    data-testid="plan-editor-field-grouper-code"
                                    className="h-7 text-xs"
                                    value={element.owner.newAsset
                                        ? element.owner.newAsset.code ?? ''
                                        : element.owner.assetCode ?? storedGrouper?.code ?? ''}
                                    placeholder={t('planEditor.field_code_placeholder')}
                                    onChange={(e) => props.onUpdate((el) => {
                                        if (el.kind !== 'space') return
                                        const next = e.target.value
                                        if (el.owner.newAsset) el.owner.newAsset.code = next
                                        else el.owner.assetCode = next
                                    })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur()
                                    }}
                                />
                            </div>
                            {props.grouperCodeError && (
                                <p
                                    className="pb-1 pl-[7.5rem] text-xs text-destructive"
                                    data-testid="plan-editor-field-grouper-code-error"
                                >
                                    {props.grouperCodeError}
                                </p>
                            )}
                        </>
                    ) : (
                        <>
                            {props.roomTypes.length > 1 ? (
                                // Which room this is — kitchen, bathroom, a
                                // bedroom (spec 2026-08-31-01 §8). Editable
                                // after the save, like a grouper's kind and
                                // unlike an opening's type: a room typed wrong
                                // on the first pass is visible from the plan
                                // and should be fixable there. The options come
                                // from the organization's own ontology.
                                <SelectField
                                    id="plan-editor-field-room-type"
                                    label={t('planEditor.field_type')}
                                    value={element.owner.newAsset
                                        ? element.owner.newAsset.typeCode
                                        // A digitized space that nobody promoted
                                        // yet owns no asset, so its owner still
                                        // reads as the bundle's — the type the
                                        // extraction proposed is on the element
                                        // (spec 2026-09-03-01 §F4).
                                        : element.owner.assetTypeEdit
                                        ?? (element.owner.assetUid ? element.owner.assetTypeCode : undefined)
                                        ?? element.typeHint
                                        ?? element.owner.assetTypeCode ?? ''}
                                    options={props.roomTypes.map((r) => ({ value: r.code, label: r.label }))}
                                    onChange={(code) => props.onUpdate((el) => {
                                        if (el.kind !== 'space') return
                                        // An un-promoted digitized space has no
                                        // asset to retype: what the select
                                        // changes is the proposal promotion will
                                        // consume (spec 2026-09-03-01 §F4).
                                        if (!el.owner.newAsset && !el.owner.assetUid && el.typeHint) {
                                            el.typeHint = code
                                            return
                                        }
                                        if (el.owner.newAsset) {
                                            el.owner.newAsset.typeCode = code
                                        } else {
                                            // Both: `assetTypeEdit` is what the
                                            // diff reads (§6), `assetTypeCode`
                                            // is what the parent rule and the
                                            // drawing read.
                                            el.owner.assetTypeEdit = code
                                            el.owner.assetTypeCode = code
                                        }
                                        // Becoming a numbered type takes the
                                        // next free number, so two bedrooms read
                                        // "Chambre 1" and "Chambre 2" without a
                                        // field being touched (spec
                                        // 2026-08-31-02 §5). Only when it has
                                        // none: retyping a numbered room to
                                        // another numbered type keeps the number
                                        // it was known by.
                                        if (!props.roomNumberTypes.has(code)) return
                                        if (ownerRoomNumber(el.owner) !== undefined) return
                                        const parentUid = el.owner.newAsset?.parentUid ?? el.owner.assetParentUid
                                        const next = props.nextRoomNumber(code, parentUid)
                                        if (el.owner.newAsset) el.owner.newAsset.roomNumber = next
                                        else el.owner.assetRoomNumberEdit = next
                                    })}
                                />
                            ) : (
                                // An org whose types predate the sub-types
                                // (spec 2026-08-31-01) has nothing to pick
                                // from — show why instead of hiding the row,
                                // so the field's label and alignment stay put
                                // once the types land (spec 2026-09-14-02).
                                <>
                                    <SelectField
                                        id="plan-editor-field-room-type"
                                        label={t('planEditor.field_type')}
                                        value={roomTypeSelectValue}
                                        options={roomTypeDisabledOptions}
                                        onChange={() => { /* disabled: nothing to pick from */ }}
                                        disabled
                                    />
                                    <p
                                        className="w-full pb-1 pl-[7.5rem] text-xs text-muted-foreground"
                                        data-testid="plan-editor-field-room-type-hint"
                                    >
                                        {t('planEditor.room_types_not_provisioned')}
                                        {canProvisionRoomTypes && (
                                            <>
                                                {' '}
                                                <Link
                                                    to="/$org/types/sync"
                                                    params={{ org: props.org }}
                                                    className="text-primary hover:underline"
                                                    data-testid="plan-editor-room-types-provision-link"
                                                >
                                                    {t('planEditor.room_types_provision_link')}
                                                </Link>
                                            </>
                                        )}
                                    </p>
                                </>
                            )}
                            {props.roomNumberTypes.has(roomTypeCode ?? '') && (
                                // Right beside the type, because the two
                                // together are what an unnamed room is called.
                                <div className="flex items-center gap-2 py-1">
                                    <Label
                                        htmlFor="plan-editor-field-room-number"
                                        className="w-28 shrink-0 text-xs text-muted-foreground"
                                    >
                                        {t('planEditor.field_room_number')}
                                    </Label>
                                    <Input
                                        id="plan-editor-field-room-number"
                                        data-testid="plan-editor-field-room-number"
                                        className="h-7 text-xs"
                                        type="number"
                                        min={1}
                                        value={ownerRoomNumber(element.owner) ?? ''}
                                        onChange={(e) => props.onUpdate((el) => {
                                            if (el.kind !== 'space') return
                                            const raw = e.target.value.trim()
                                            const n = raw === '' ? undefined : Number(raw)
                                            const next = n !== undefined && Number.isFinite(n) ? n : undefined
                                            if (el.owner.newAsset) {
                                                if (next === undefined) delete el.owner.newAsset.roomNumber
                                                else el.owner.newAsset.roomNumber = next
                                            } else if (next !== undefined) {
                                                el.owner.assetRoomNumberEdit = next
                                            }
                                        })}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur()
                                        }}
                                    />
                                </div>
                            )}
                            <SpaceGrouperField
                                grouper={element.owner.grouper}
                                error={props.grouperError}
                                options={props.grouperOptions}
                                recentUids={props.recentGrouperUids}
                                typeLabelOf={props.typeLabelOf}
                                onAssign={props.onAssignGrouper}
                                onCreate={props.onCreateGrouper}
                            />
                        </>
                    )}
                </>
            )}

            {element.kind === 'opening' && (() => {
                const semantics = normalizeOpeningSemantics(element)
                const directional = DIRECTIONAL_OPERATIONS.includes(semantics.operation)
                return (
                    <>
                        {element.owner.newAsset ? (
                            // The type is only editable while the asset is still
                            // pending: retyping an asset that already exists is an
                            // inventory operation, not a drawing one, and belongs
                            // on the asset page rather than in a geometry editor.
                            <SelectField
                                id="plan-editor-field-opening-type"
                                label={t('planEditor.field_type')}
                                value={element.owner.newAsset.typeCode}
                                options={props.openingTypes.map((o) => ({ value: o.code, label: o.label }))}
                                onChange={(code) => updateTyped((el) => {
                                    if (el.kind !== 'opening' || !el.owner.newAsset) return
                                    el.openingKind = code.includes('window') ? 'window' : 'door'
                                    el.owner.newAsset.typeCode = code
                                    el.owner.newAsset.name = props.typeLabelOf(code)
                                })}
                                after={mixedHint}
                            />
                        ) : (
                            <div className="flex items-center gap-2 py-1 text-xs">
                                <span className="w-28 shrink-0 text-muted-foreground">{t('planEditor.field_type')}</span>
                                <span className="text-foreground" data-testid="plan-editor-field-opening-type">
                                    {element.owner.assetTypeCode ? props.typeLabelOf(element.owner.assetTypeCode) : '—'}
                                </span>
                            </div>
                        )}
                        <NumberField
                            id="plan-editor-field-width"
                            label={t('planEditor.field_width')}
                            value={Math.round(props.toMm(element.width))}
                            step={10}
                            min={50}
                            onCommit={(v) => props.onUpdate((el) => {
                                if (el.kind === 'opening') el.width = props.toUnits(v)
                            })}
                        />
                        <SelectField
                            id="plan-editor-field-operation"
                            label={t('planEditor.field_operation')}
                            value={semantics.operation}
                            options={OPERATIONS.map((o) => ({ value: o, label: t(`planEditor.operations.${o}`) }))}
                            onChange={(v) => props.onUpdate((el) => {
                                if (el.kind !== 'opening') return
                                el.operation = v as Operation
                                if (DIRECTIONAL_OPERATIONS.includes(v as Operation) && !el.swing) el.swing = 'right'
                            })}
                        />
                        {directional && (
                            <SelectField
                                id="plan-editor-field-swing"
                                label={t('planEditor.field_swing')}
                                value={element.swing ?? 'right'}
                                options={SWINGS.map((s) => ({ value: s, label: t(`planEditor.swings.${s}`) }))}
                                onChange={(v) => props.onUpdate((el) => {
                                    if (el.kind === 'opening') el.swing = v as Swing
                                })}
                            />
                        )}
                        {semantics.operation !== 'none' && (
                            <SelectField
                                id="plan-editor-field-leaves"
                                label={t('planEditor.field_leaves')}
                                value={String(semantics.leaves)}
                                options={[{ value: '1', label: '1' }, { value: '2', label: '2' }]}
                                onChange={(v) => props.onUpdate((el) => {
                                    if (el.kind === 'opening') el.leaves = v === '2' ? 2 : 1
                                })}
                            />
                        )}
                        {directional && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-1 h-7 text-xs"
                                data-testid="plan-editor-flip-side"
                                title={t('planEditor.flip_side_hint')}
                                onClick={() => props.onUpdate((el) => {
                                    if (el.kind === 'opening') flipOpeningSide(el)
                                })}
                            >
                                <AppIcon path={mdiSwapHorizontal} size="xs" className="mr-1" />
                                {t('planEditor.flip_side')}
                            </Button>
                        )}
                        <p className="pt-1 text-[11px] text-muted-foreground">
                            {t(`planEditor.operation_hints.${semantics.operation}`)}
                        </p>
                    </>
                )
            })()}

            {element.kind === 'equipment' && (
                <>
                    <div className="flex flex-wrap items-center gap-2 py-1">
                        <span className="w-28 shrink-0 text-xs text-muted-foreground">{t('planEditor.field_type')}</span>
                        <PlanEquipmentTypePicker
                            org={props.org}
                            open={props.pickerOpen}
                            onOpenChange={props.onPickerOpenChange}
                            onPick={(picked) => {
                                props.onPickEquipmentType(picked)
                                props.onPickerOpenChange(false)
                            }}
                        >
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 flex-1 justify-start text-xs"
                                data-testid="plan-editor-type-picker"
                                title={element.typeCode}
                            >
                                {element.typeCode ? props.typeLabelOf(element.typeCode) : t('planEditor.choose_type')}
                            </Button>
                        </PlanEquipmentTypePicker>
                        {mixedHint}
                    </div>
                    <NumberField
                        id="plan-editor-field-equipment-width"
                        label={t('planEditor.field_width')}
                        value={Math.round(props.toMm(element.width))}
                        step={10}
                        min={10}
                        onCommit={(v) => props.onEquipmentSize({ width: v, depth: Math.round(props.toMm(element.depth)) })}
                    />
                    <NumberField
                        id="plan-editor-field-equipment-depth"
                        label={t('planEditor.field_depth')}
                        value={Math.round(props.toMm(element.depth))}
                        step={10}
                        min={10}
                        onCommit={(v) => props.onEquipmentSize({ width: Math.round(props.toMm(element.width)), depth: v })}
                    />
                    <div className="flex gap-2 pb-1">
                        <span className="w-28 shrink-0" aria-hidden />
                        <EquipmentSizeChips
                            recent={props.recentSizes}
                            active={{ width: Math.round(props.toMm(element.width)), depth: Math.round(props.toMm(element.depth)) }}
                            onPick={props.onEquipmentSizePreset}
                        />
                        {/* The chips only appear at two remembered sizes, and a
                          * badge beside an empty row would explain nothing. */}
                        {props.recentSizes.length > 1 && mixedHint}
                    </div>
                    <NumberField
                        id="plan-editor-field-rotation"
                        label={t('planEditor.field_rotation')}
                        value={Math.round(element.rotationDeg)}
                        step={15}
                        onCommit={(v) => props.onUpdate((el) => {
                            if (el.kind === 'equipment') el.rotationDeg = ((v % 360) + 360) % 360
                        })}
                    />
                    {/* The equipment it is a component of (spec 2026-10-27-17). */}
                    {element.owner.assetUid && !element.typeCode?.endsWith('_network') && (
                        <AssemblyControl org={props.org} assetUid={element.owner.assetUid} />
                    )}
                </>
            )}
        </div>
    )
}
